import React, { Fragment, useState } from 'react';
import { Popover, Dialog, Transition } from '@headlessui/react';
import { XIcon, SearchIcon, BellIcon, ChevronRightIcon } from '@heroicons/react/solid';
import Navlink from 'next/link';
import { CoursePanel } from './CoursePanel';
import { PlannerPanel } from './PlannerPanel';
import { SlideOutMenu } from '../sidebar/SlideOutMenu';
import { TransitionPopover } from '../common/TransitionPopover';
import NavLink from 'next/link';
import NextImage from 'next/image';
import { Button, SkipperLogo } from '@components/base';
import { classNames } from '@utils/classNames';
import {
    SideBarStates,
    useSidebarDispatch,
    useSidebarState,
} from '@components/context/SidebarContext';
import { useUserSession } from '@hooks/user/useDefaultSession';
import { Permissions, Roles, useUserPermissions } from '@hooks/user/useUserPermissions';
import { useActiveRoute } from '@hooks/useActiveRoute';
import { SkipperAvatar } from '@skipper/ui';
import { FileEntry, useAccountDetailsQuery } from '@graphql/generated-graphql';
import { useFileEntry } from '@hooks/files/useFileEntry';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudDownload } from '@fortawesome/pro-solid-svg-icons';
import { useRouter } from 'next/router';
import { getClient } from '@graphql/graphql-client/graphClient';

export function Header() {
    const [user] = useUserSession();
    const router = useRouter();
    const isActivePath = useActiveRoute();
    const { atleastOne, notChild, hasPermission } = useUserPermissions();
    const { data: accountData } = useAccountDetailsQuery(getClient('users'), undefined, {
        staleTime: 60 * 1000 * 5,
    });
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    return (
        <header className="flex-shrink-0 relative h-16 bg-white flex items-center overflow-visible border-b borer-gray-200 print:hidden">
            {/* Logo area */}
            <HeaderLogo />
            {/* Desktop nav area */}
            <Popover className="relative h-full hidden sm:min-w-0 sm:flex-1 sm:flex sm:items-center sm:justify-between px-4">
                <Popover.Group as="nav" className="sm:flex space-x-6 relative pl-6">
                    <Popover className="group relative h-16">
                        {({ open }) => (
                            <>
                                <MenuButton
                                    open={open}
                                    label="Courses"
                                    active={isActivePath(['/', 'courses'])}
                                />
                                <TransitionPopover open={open}>
                                    <CoursePanel />
                                </TransitionPopover>
                            </>
                        )}
                    </Popover>
                    {notChild && (
                        <Navlink key="Templates" href="/templates">
                            <div
                                className={`relative ml-1 w-full px-4 flex items-center cursor-pointer hover:bg-teal-050 ${
                                    isActivePath(['templates']) ? 'border-b-2 border-teal-600' : ''
                                }`}
                            >
                                <p className="text-base font-medium text-passport">Templates</p>
                            </div>
                        </Navlink>
                    )}
                    {atleastOne([
                        Permissions.planner_day,
                        Permissions.planner_week,
                        Permissions.planner_month,
                        Permissions.planner_schedule,
                        Permissions.planner_grid,
                        Permissions.planner_flow,
                        Permissions.planner_boards,
                    ]) && (
                        <Navlink key="Planner" href="/coursegrid">
                            <div
                                className={`relative ml-1 w-full px-4 flex items-center cursor-pointer hover:bg-teal-050 ${
                                    isActivePath(['planner']) ? 'border-b-2 border-teal-600' : ''
                                }`}
                            >
                                <p className="text-base font-medium text-passport">Planner</p>
                            </div>
                        </Navlink>

                        // <Popover className="group relative h-16">
                        //     {({ open }) => (
                        //         <React.Fragment>
                        //             <MenuButton
                        //                 open={open}
                        //                 label="Planner"
                        //                 active={isActivePath(['planner', 'workflow'])}
                        //             />
                        //             <TransitionPopover open={open}>
                        //                 <PlannerPanel />
                        //             </TransitionPopover>
                        //         </React.Fragment>
                        //     )}
                        // </Popover>
                    )}
                    {hasPermission(Permissions.skipper_community) && (
                        <Navlink key="Community" href={isSafari ? '' : '/community'}>
                            <div
                                className={`relative ml-1 w-full px-4 flex items-center cursor-pointer hover:bg-teal-050 ${
                                    isActivePath(['community']) ? 'border-b-2 border-teal-600' : ''
                                }`}
                                onClick={(e) => {
                                    if (isSafari) {
                                        e.preventDefault();
                                        window.open(
                                            'https://community.skipper.io/', // '?automatic_login=true', - this will be needed for SSO
                                            '_blank',
                                        );
                                        return false;
                                    }
                                }}
                            >
                                <p className="text-base font-medium text-passport">Community</p>
                            </div>
                        </Navlink>
                    )}
                </Popover.Group>
                <div
                    className={`mr-16 text-xs text-right text-orange-500 cursor-pointer flex gap-4 align-middle`}
                >
                    {!navigator.userAgent.includes('Chrome') && (
                        <a
                            target="_blank"
                            href="https://www.google.com/chrome/"
                            rel="noopener noreferrer"
                        >
                            Skipper works best
                            <br />
                            in Google Chrome
                            <FontAwesomeIcon icon={faCloudDownload} className={`ml-2`} />
                        </a>
                    )}
                    {(accountData?.account?.subscriptionPlan?.productId || 'business') !=
                        'business' &&
                        user.accountRole.roleType == Roles.Adult && (
                            <Button
                                variant="subtle"
                                className="px-2"
                                size={'small'}
                                onClick={() => router.push('/account/billing')}
                                type="submit"
                            >
                                Upgrade
                            </Button>
                        )}
                </div>
            </Popover>
            <SlideOutMenu />
            <SidebarTarget />
        </header>
    );
}

function HeaderLogo() {
    return (
        <div className="flex inset-y-0 left-0 md:static md:flex-shrink-0 justify-center items-center border-b border-gray-200">
            <NavLink href="/">
                <div className="flex items-center justify-center cursor-pointer">
                    <div className="flex items-center justify-center h-16 w-16 bg-white focus:outline-none focus:ring-2 focus:ring-inset md:w-20">
                        <SkipperLogo className="fill-current text-primary h-8 w-8" />
                    </div>
                </div>
            </NavLink>
        </div>
    );
}

function MenuButton({ open, label, active }: { open: boolean; active: boolean; label: string }) {
    const isActive = open || active;
    return (
        <Popover.Button
            className={classNames(
                isActive ? 'text-passport ' : 'text-passport',
                'relative h-full w-full px-4 items-center text-base font-medium group-hover:bg-teal-050 ',
            )}
        >
            {isActive && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600" />}
            <span>{label}</span>
        </Popover.Button>
    );
}

function SidebarTarget() {
    const { toggle } = useSidebarDispatch();
    const { state } = useSidebarState();
    const [user] = useUserSession();
    const imageUrl = useFileEntry({ file: user?.imageFileEntry as FileEntry });
    return (
        <div
            className={classNames(
                'absolute z-[500] inset-y-0 right-0 min-w-14 cursor-pointer',
                state !== SideBarStates.Profile && state !== SideBarStates.Closed
                    ? 'border-l border-gray-300'
                    : 'z-0',
            )}
            onClick={(e: any) => {
                e.preventDefault?.();
                toggle(SideBarStates.Profile);
            }}
        >
            <div className="relative flex items-center justify-center h-16 w-14 bg-white focus:outline-none md:w-14 border-gray-200">
                {state !== SideBarStates.Profile ? (
                    <SkipperAvatar {...user} imageUrl={imageUrl} size="large" />
                ) : (
                    <ChevronRightIcon className="w-8 h-8" />
                )}
            </div>
        </div>
    );
}
